// Service requests (§13.5): raise a query, request or complaint and track
// the ones already raised.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { createServiceRequest, listServiceRequests } from '@/api/profile';
import { ApiError } from '@/api/client';
import type { ServiceRequest, ServiceRequestKind } from '@/types/phase2.types';
import { formatLong } from '@/mock/clock';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import './profile.css';

const KINDS: ServiceRequestKind[] = ['Query', 'Request', 'Complaint'];
const CATEGORIES = ['Account', 'Payments', 'Beneficiaries', 'UPI', 'Statement', 'App', 'Other'];

const STATUS_CLASS: Record<ServiceRequest['status'], string> = {
  OPEN: 'nova-sr-open',
  IN_PROGRESS: 'nova-sr-progress',
  RESOLVED: 'nova-sr-resolved',
};

const STATUS_LABEL: Record<ServiceRequest['status'], string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
};

const ServiceRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ServiceRequest[] | null>(null);
  const [kind, setKind] = useState<ServiceRequestKind>('Query');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listServiceRequests()
      .then(setRequests)
      .catch(() => setRequests([]));
  }, []);

  const submit = async () => {
    if (!description.trim()) {
      setError('Tell us what you need in a few words.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const created = await createServiceRequest({ kind, category, description: description.trim() });
      setRequests((list) => [created, ...(list ?? [])]);
      setDescription('');
      toast.success(`${kind} raised · ${created.ref}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not raise the request. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="nova-profile-page">
      <div className="nova-pay-flow-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={() => navigate('/profile')}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>Service requests</h2>
      </div>

      <div className="nova-profile-rows mb-3 p-3">
        <div className="row g-2">
          <div className="col-12 col-sm-4">
            <label className="form-label">Type</label>
            <select
              className="form-select"
              value={kind}
              onChange={(e) => setKind(e.target.value as ServiceRequestKind)}
            >
              {KINDS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-sm-8">
            <label className="form-label">Category</label>
            <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="What do you need help with?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        {error && <div className="nova-limit-box mt-3 mb-2">{error}</div>}
        <div className="d-flex justify-content-end py-2">
          <Button onClick={() => void submit()} isLoading={submitting}>
            Raise {kind.toLowerCase()}
          </Button>
        </div>
      </div>

      <h3 className="nova-pay-section-title">Your requests</h3>
      {!requests && <Loader rows={3} />}
      {requests && requests.length === 0 && (
        <p className="nova-pay-empty-line">Nothing raised yet. Your queries, requests and complaints show here.</p>
      )}
      {(requests ?? []).map((r) => (
        <div key={r.ref} className="nova-nominee-row">
          <span className="nova-ben-row-text">
            <span className="nova-ben-alias">
              {r.kind} · {r.category}
            </span>
            <span className="nova-ben-count">{r.description}</span>
            <span className="nova-ben-count">
              {r.ref} · {formatLong(r.createdAt)}
            </span>
          </span>
          <span className={`nova-sr-status ${STATUS_CLASS[r.status]}`}>{STATUS_LABEL[r.status]}</span>
        </div>
      ))}
    </div>
  );
};

export default ServiceRequests;
